/**
 * Membership validation – phone or membership number via Mechanic webhook.
 *
 * Success/error UI is printed by Liquid from customer tags / metafields.
 * After submit, JS polls this section via the Section Rendering API (no full
 * page flashes). When a fresh result appears, it does one clean page reload.
 */
(function () {
  'use strict';

  var FORM_SELECTOR = '[data-jsc-membership-form]';
  var AWAIT_KEY = 'jsc_mv_awaiting_result';
  var ATTEMPT_KEY = 'jsc_mv_await_attempts';
  var SEEN_EMPTY_KEY = 'jsc_mv_seen_empty';
  var BASELINE_KEY = 'jsc_mv_baseline';
  var MAX_ATTEMPTS = 12;
  var TH_PHONE_PATTERN = /^0\d{9}$/;
  var DIAL_CODE_PATTERN = /^\d{1,2}$/;
  var MEMBERSHIP_PATTERN = /^[A-Za-z0-9-]{4,40}$/;

  var DEFAULT_MESSAGES = {
    invalid_phone: 'Please enter a valid 10-digit Thai mobile number starting with 0.',
    invalid_code: 'Please enter a valid country code (1-2 digits).',
    invalid_membership: 'Please enter a valid membership number.',
    missing_input: 'Please enter a mobile number or membership number.',
    generic: 'Something went wrong. Please try again.',
    success: 'Your membership has been verified successfully.',
    pending: 'Verifying…',
    overlay: 'Verifying your membership. Please wait…',
    pending_result: 'Verification is still processing. Please try again in a moment.'
  };

  function digitsOnly(value) {
    return String(value == null ? '' : value).replace(/\D/g, '');
  }

  function membershipValue(value) {
    return String(value == null ? '' : value)
      .trim()
      .replace(/\s+/g, '');
  }

  function readJson(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function resultSignature(status, errorMessage) {
    return String(status || '') + '|' + String(errorMessage || '');
  }

  function readMessages(form) {
    var messages = {};
    var key;
    var parsed = readJson(form.querySelector('[data-jsc-mv-messages]'));

    for (key in DEFAULT_MESSAGES) {
      if (Object.prototype.hasOwnProperty.call(DEFAULT_MESSAGES, key)) {
        messages[key] = DEFAULT_MESSAGES[key];
      }
    }

    if (!parsed) return messages;

    for (key in parsed) {
      if (Object.prototype.hasOwnProperty.call(parsed, key) && parsed[key]) {
        messages[key] = parsed[key];
      }
    }

    return messages;
  }

  function MembershipValidationForm(form) {
    this.form = form;
    this.phoneInput = form.querySelector('[data-jsc-mv-phone]');
    this.codeInput = form.querySelector('[data-jsc-mv-code]');
    this.membershipInput = form.querySelector('[data-jsc-mv-membership]');
    this.submitButton = form.querySelector('[data-jsc-mv-submit]');
    this.submitLabel = form.querySelector('[data-jsc-mv-submit-label]');
    this.errorNotice = form.querySelector('[data-jsc-mv-status="error"]');
    this.successNotice = form.querySelector('[data-jsc-mv-status="success"]');
    this.overlay = document.querySelector('[data-jsc-mv-overlay]');
    this.overlayText = document.querySelector('[data-jsc-mv-overlay-text]');
    this.endpoint = form.dataset.endpoint;
    this.sectionId = form.dataset.sectionId || '';
    this.pollDelay = parseInt(form.dataset.pollDelay || form.dataset.reloadDelay, 10) || 5000;
    this.messages = readMessages(form);
    this.result = readJson(form.querySelector('[data-jsc-mv-result]')) || {};
    this.idleLabel = this.submitLabel ? this.submitLabel.textContent : '';
    this.isSubmitting = false;
    this.pollTimer = null;

    this.bind();
    this.applyPendingResult();
  }

  MembershipValidationForm.prototype.bind = function () {
    var self = this;

    [this.phoneInput, this.codeInput].forEach(function (input) {
      if (!input) return;

      input.addEventListener('input', function () {
        self.sanitizeDigits(input);
        self.clearNotices();
      });

      input.addEventListener('paste', function (event) {
        event.preventDefault();
        var clipboard = event.clipboardData || window.clipboardData;
        var pasted = clipboard ? clipboard.getData('text') : '';
        var maxLength = parseInt(input.getAttribute('maxlength'), 10) || 10;
        input.value = digitsOnly(pasted).slice(0, maxLength);
        self.clearNotices();
      });
    });

    if (this.membershipInput) {
      this.membershipInput.addEventListener('input', function () {
        self.sanitizeMembership(self.membershipInput);
        self.clearNotices();
      });

      this.membershipInput.addEventListener('paste', function (event) {
        event.preventDefault();
        var clipboard = event.clipboardData || window.clipboardData;
        var pasted = clipboard ? clipboard.getData('text') : '';
        var maxLength = parseInt(self.membershipInput.getAttribute('maxlength'), 10) || 40;
        self.membershipInput.value = membershipValue(pasted)
          .replace(/[^A-Za-z0-9-]/g, '')
          .slice(0, maxLength);
        self.clearNotices();
      });
    }

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submit();
    });
  };

  MembershipValidationForm.prototype.sanitizeDigits = function (input) {
    var caret = input.selectionStart;
    var original = input.value;
    var cleaned = digitsOnly(original);

    if (cleaned === original) return;

    input.value = cleaned;

    if (caret !== null && typeof input.setSelectionRange === 'function') {
      var offset = Math.max(caret - (original.length - cleaned.length), 0);
      input.setSelectionRange(offset, offset);
    }
  };

  MembershipValidationForm.prototype.sanitizeMembership = function (input) {
    var caret = input.selectionStart;
    var original = input.value;
    var cleaned = membershipValue(original).replace(/[^A-Za-z0-9-]/g, '');

    if (cleaned === original) return;

    input.value = cleaned;

    if (caret !== null && typeof input.setSelectionRange === 'function') {
      var offset = Math.max(caret - (original.length - cleaned.length), 0);
      input.setSelectionRange(offset, offset);
    }
  };

  MembershipValidationForm.prototype.showNotice = function (notice, message) {
    if (!notice) return;
    var text = notice.querySelector('[data-jsc-mv-status-text]');
    if (text) text.textContent = message;
    notice.hidden = false;
  };

  MembershipValidationForm.prototype.clearNotices = function () {
    if (this.errorNotice) this.errorNotice.hidden = true;
    if (this.successNotice) this.successNotice.hidden = true;
    if (this.phoneInput) this.phoneInput.classList.remove('invalid');
    if (this.codeInput) this.codeInput.classList.remove('invalid');
    if (this.membershipInput) this.membershipInput.classList.remove('invalid');
  };

  MembershipValidationForm.prototype.setPending = function (isPending) {
    this.isSubmitting = isPending;
    if (this.submitButton) this.submitButton.disabled = isPending;
    if (this.submitLabel) {
      this.submitLabel.textContent = isPending ? this.messages.pending : this.idleLabel;
    }
  };

  MembershipValidationForm.prototype.showOverlay = function () {
    if (this.overlayText) this.overlayText.textContent = this.messages.overlay;
    if (this.overlay) {
      this.overlay.hidden = false;
      document.documentElement.classList.add('jsc-mv-overlay-open');
    }
  };

  MembershipValidationForm.prototype.hideOverlay = function () {
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.classList.remove('jsc-mv-overlay-open');
  };

  MembershipValidationForm.prototype.clearAwaitState = function () {
    if (this.pollTimer) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    try {
      window.sessionStorage.removeItem(AWAIT_KEY);
      window.sessionStorage.removeItem(ATTEMPT_KEY);
      window.sessionStorage.removeItem(SEEN_EMPTY_KEY);
      window.sessionStorage.removeItem(BASELINE_KEY);
    } catch (error) {
      // Ignore storage errors.
    }
  };

  MembershipValidationForm.prototype.getAwaitAttempts = function () {
    try {
      return parseInt(window.sessionStorage.getItem(ATTEMPT_KEY), 10) || 0;
    } catch (error) {
      return 0;
    }
  };

  MembershipValidationForm.prototype.setAwaitAttempts = function (count) {
    try {
      window.sessionStorage.setItem(ATTEMPT_KEY, String(count));
    } catch (error) {
      // Ignore storage errors.
    }
  };

  MembershipValidationForm.prototype.hasSeenEmpty = function () {
    try {
      return window.sessionStorage.getItem(SEEN_EMPTY_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  MembershipValidationForm.prototype.markSeenEmpty = function () {
    try {
      window.sessionStorage.setItem(SEEN_EMPTY_KEY, '1');
    } catch (error) {
      // Ignore storage errors.
    }
  };

  MembershipValidationForm.prototype.getBaseline = function () {
    try {
      return window.sessionStorage.getItem(BASELINE_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  MembershipValidationForm.prototype.getStatus = function () {
    return this.form.dataset.verifyStatus || this.result.status || '';
  };

  MembershipValidationForm.prototype.getErrorMessage = function () {
    return this.result.error_message || '';
  };

  MembershipValidationForm.prototype.finishAwaiting = function (fallbackErrorMessage) {
    var status = this.getStatus();

    this.clearAwaitState();
    this.hideOverlay();
    this.setPending(false);

    // Success/error copy is printed by Liquid from customer tags.
    if (status === 'success' || status === 'error') return;

    this.clearNotices();
    this.showNotice(this.errorNotice, fallbackErrorMessage || this.messages.pending_result);
  };

  MembershipValidationForm.prototype.fetchSectionHtml = function () {
    if (!this.sectionId) {
      return Promise.reject(new Error('missing section id'));
    }

    var url =
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(this.sectionId) +
      '&_ts=' +
      Date.now();

    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html'
      }
    }).then(function (response) {
      if (!response.ok) {
        var error = new Error('section fetch failed');
        error.status = response.status;
        throw error;
      }
      return response.text();
    });
  };

  MembershipValidationForm.prototype.parseStatusFromHtml = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var form = doc.querySelector(FORM_SELECTOR);

    if (!form) {
      return {
        status: '',
        errorMessage: '',
        verified: false
      };
    }

    var result = readJson(form.querySelector('[data-jsc-mv-result]')) || {};

    return {
      status: form.dataset.verifyStatus || result.status || '',
      errorMessage: result.error_message || '',
      verified: form.dataset.verified === 'true'
    };
  };

  MembershipValidationForm.prototype.schedulePoll = function () {
    var self = this;

    this.showOverlay();
    this.setPending(true);

    if (this.pollTimer) {
      window.clearTimeout(this.pollTimer);
    }

    this.pollTimer = window.setTimeout(function () {
      self.pollTimer = null;
      self.pollVerification();
    }, self.pollDelay);
  };

  MembershipValidationForm.prototype.evaluatePollResult = function (parsed) {
    var status = parsed.status || '';
    var signature = resultSignature(status, parsed.errorMessage);
    var baseline = this.getBaseline();
    var attempts = this.getAwaitAttempts() + 1;
    this.setAwaitAttempts(attempts);

    if (status !== 'success' && status !== 'error') {
      this.markSeenEmpty();
      if (attempts >= MAX_ATTEMPTS) {
        this.finishAwaiting(this.messages.pending_result);
        return;
      }
      this.schedulePoll();
      return;
    }

    // Avoid stopping on a stale pre-submit tag; wait for clear or a changed result.
    var isFreshResult =
      this.hasSeenEmpty() ||
      signature !== baseline ||
      parsed.verified === true ||
      attempts >= 2;

    if (!isFreshResult) {
      if (attempts >= MAX_ATTEMPTS) {
        this.finishAwaiting(this.messages.pending_result);
        return;
      }
      this.schedulePoll();
      return;
    }

    // Fresh result — one clean reload for the verified / error UI.
    this.clearAwaitState();
    window.location.reload();
  };

  MembershipValidationForm.prototype.pollVerification = function () {
    var self = this;

    this.fetchSectionHtml()
      .then(function (html) {
        self.evaluatePollResult(self.parseStatusFromHtml(html));
      })
      .catch(function () {
        var attempts = self.getAwaitAttempts() + 1;
        self.setAwaitAttempts(attempts);

        if (attempts >= MAX_ATTEMPTS) {
          self.finishAwaiting(self.messages.pending_result);
          return;
        }

        self.schedulePoll();
      });
  };

  MembershipValidationForm.prototype.applyPendingResult = function () {
    var awaiting = false;

    try {
      awaiting = window.sessionStorage.getItem(AWAIT_KEY) === '1';
    } catch (error) {
      awaiting = false;
    }

    if (!awaiting) return;

    // Resume quiet section polling after an accidental mid-wait refresh.
    this.schedulePoll();
  };

  MembershipValidationForm.prototype.validate = function () {
    var phone = this.phoneInput ? digitsOnly(this.phoneInput.value) : '';
    var code = this.codeInput ? digitsOnly(this.codeInput.value) : '';
    var membership = this.membershipInput ? membershipValue(this.membershipInput.value) : '';
    var hasPhone = phone !== '';
    var hasMembership = membership !== '';

    if (!hasPhone && !hasMembership) {
      if (this.phoneInput) {
        this.phoneInput.classList.add('invalid');
        this.phoneInput.focus();
      }
      if (this.membershipInput) this.membershipInput.classList.add('invalid');
      this.showNotice(this.errorNotice, this.messages.missing_input);
      return null;
    }

    if (hasPhone && !TH_PHONE_PATTERN.test(phone)) {
      if (this.phoneInput) {
        this.phoneInput.classList.add('invalid');
        this.phoneInput.focus();
      }
      this.showNotice(this.errorNotice, this.messages.invalid_phone);
      return null;
    }

    if (hasPhone && code !== '' && !DIAL_CODE_PATTERN.test(code)) {
      if (this.codeInput) {
        this.codeInput.classList.add('invalid');
        this.codeInput.focus();
      }
      this.showNotice(this.errorNotice, this.messages.invalid_code);
      return null;
    }

    if (hasMembership && !MEMBERSHIP_PATTERN.test(membership)) {
      if (this.membershipInput) {
        this.membershipInput.classList.add('invalid');
        this.membershipInput.focus();
      }
      this.showNotice(this.errorNotice, this.messages.invalid_membership);
      return null;
    }

    return {
      phone_number: phone,
      country_dial_code: hasPhone ? code : '',
      membership_number: membership,
      customerId: this.form.dataset.customerId || ''
    };
  };

  MembershipValidationForm.prototype.beginVerificationWait = function () {
    try {
      window.sessionStorage.setItem(AWAIT_KEY, '1');
      window.sessionStorage.setItem(ATTEMPT_KEY, '0');
      window.sessionStorage.setItem(SEEN_EMPTY_KEY, '0');
      window.sessionStorage.setItem(
        BASELINE_KEY,
        resultSignature(this.getStatus(), this.getErrorMessage())
      );
    } catch (error) {
      // Polling still runs; result may not resume after refresh without the flag.
    }

    this.schedulePoll();
  };

  MembershipValidationForm.prototype.submit = function () {
    if (this.isSubmitting) return;

    if (this.form.dataset.verified === 'true') {
      return;
    }

    this.clearNotices();

    var payload = this.validate();
    if (!payload) return;

    if (!this.endpoint) {
      this.showNotice(this.errorNotice, this.messages.generic);
      return;
    }

    var self = this;
    this.setPending(true);
    this.showOverlay();

    fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'omit',
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        // Mechanic webhooks always respond 204 No Content (no JSON body).
        if (response.status === 204 || response.status === 200) {
          return {};
        }

        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            if (!response.ok || body.ok === false) {
              var error = new Error(body.message || self.messages.generic);
              error.status = response.status;
              throw error;
            }
            return body;
          });
      })
      .then(function () {
        self.beginVerificationWait();
      })
      .catch(function (error) {
        console.warn(
          '[membership-validation] Webhook failed.',
          'endpoint:', self.endpoint,
          'status:', error.status || 'network error'
        );
        self.clearAwaitState();
        self.hideOverlay();
        self.setPending(false);
        self.showNotice(self.errorNotice, error.message || self.messages.generic);
      });
  };

  function init(root) {
    var scope = root || document;
    var forms = scope.querySelectorAll(FORM_SELECTOR);

    Array.prototype.forEach.call(forms, function (form) {
      if (form.dataset.jscMvBound === 'true') return;
      form.dataset.jscMvBound = 'true';
      new MembershipValidationForm(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
